/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useState, useEffect, useMemo} from 'react'
import {detectInIframe, isRefObject} from '../utils/utils'
import useEffectEvent from './use-effect-event'

/**
 * Returns a boolean to indicate if an element is visible on the screen. Fall back to `true`
 * if IntersectionObserver is not supported.
 * https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver
 * @param {Object|Element} ref - element ref
 * @param {Object} options
 * @param {Object} options.root - See IntersectionObserver options
 * @param {string} options.rootMargin - See IntersectionObserver options
 * @param {number|Array<number>} options.threshold - See IntersectionObserver options
 * @param {boolean} options.useOnce - Detach the observer after the element appears.
 * @returns {boolean}
 */
const useIntersectionObserver = (ref, options = {}) => {
    const [isIntersecting, setIntersecting] = useState()

    const targetElement = isRefObject(ref) ? ref.current : ref

    const {useOnce, stringifiedOptions} = useMemo(() => {
        const {useOnce, ...ioOptions} = options

        const stringifiedOptions = JSON.stringify(ioOptions)

        return {useOnce, stringifiedOptions}
    }, [options])

    const onIntersection = useEffectEvent(([entry], observer) => {
        const onScreen = entry.isIntersecting
        setIntersecting(onScreen)
        if (useOnce && onScreen) {
            observer.disconnect()
        }
    })
    useEffect(() => {
        if (!targetElement) return

        // Just set `isIntersecting` true if browser doesn't implement IntersectionObserver. If the use-case
        // is critical and you need to support very old browsers, a polyfill will need to be added.
        if (
            !('IntersectionObserver' in window) ||
            !('IntersectionObserverEntry' in window) ||
            !('intersectionRatio' in window.IntersectionObserverEntry.prototype)
        ) {
            setIntersecting(true)

            // We want to return early, but `useEffect` expects a function as the return value,
            // so we just return a noop function.
            return () => null
        }

        // parse the stringified options back to an object instead of using the original object
        // to prevent the linter asking to put the object in the dependency array
        const options = JSON.parse(stringifiedOptions)

        // Inside cross-origin iframes, root margin doesn't work unless a root element is provided.
        // ref: https://www.w3.org/TR/intersection-observer/#dom-intersectionobserver-rootmargin
        if (options.rootMargin && !options.root && detectInIframe()) {
            options.root = document.querySelector(':root')
        }

        const observer = new IntersectionObserver(onIntersection, options)

        observer.observe(targetElement)

        // Remove the observer as soon as the component is unmounted
        return () => {
            observer.disconnect()
        }
    }, [targetElement, stringifiedOptions, onIntersection])

    return isIntersecting
}

export default useIntersectionObserver
